/**
 * PDF -> PNG in the browser via pdf.js.
 *
 * Rendering client-side means the Lambda needs no PDF toolchain, the document
 * bytes never pass through the API, and — the part that matters for a fair
 * comparison — the user sees exactly the pixels the models were given.
 */

import * as pdfjs from "pdfjs-dist";
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";

pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

export interface RenderedPage {
  base64: string;
  /** Pixels of the rendered image, at SCALE below. */
  width: number;
  height: number;
  /**
   * The page's size in PDF POINTS, which is a different thing and both are
   * needed. dots.ocr returns boxes in the pixels of the image it was given;
   * OpenDataLoader returns them in points, with the origin at the BOTTOM left.
   * Overlaying either on the same canvas needs the space it was measured in.
   *
   * OPTIONAL because a page reconstructed from a SAVED run genuinely does not
   * have them: the OCR bench stores the rendered PNG and its pixel size, which
   * is all its own overlay needs. Anything drawing in point space must handle
   * their absence rather than assume a number is there.
   */
  pointsWidth?: number;
  pointsHeight?: number;
}

/**
 * 200 DPI equivalent (scale 2.78 over pdf.js's 72dpi default). Matches the
 * production extractor's DOTS_OCR_DPI, so what is measured here is comparable
 * to what the pipeline actually does — and dots.ocr's pixel bboxes depend on it.
 */
const SCALE = 200 / 72;

export async function renderPdfPage(data: ArrayBuffer, pageNumber: number) {
  // pdf.js transfers (neuters) the buffer it is given; hand it a copy so the
  // caller can still render other pages from the same upload.
  const doc = await pdfjs.getDocument({ data: data.slice(0) }).promise;
  const p = await doc.getPage(pageNumber);
  const viewport = p.getViewport({ scale: SCALE });
  // scale 1 is 72dpi, i.e. one unit per PDF point — the space OpenDataLoader
  // reports its bounding boxes in.
  const points = p.getViewport({ scale: 1 });

  const canvas = document.createElement("canvas");
  canvas.width = Math.floor(viewport.width);
  canvas.height = Math.floor(viewport.height);
  await p.render({ canvasContext: canvas.getContext("2d")!, viewport }).promise;

  const base64 = canvas.toDataURL("image/png").split(",")[1];
  return {
    page: {
      base64,
      width: canvas.width,
      height: canvas.height,
      pointsWidth: points.width,
      pointsHeight: points.height,
    } as RenderedPage,
    count: doc.numPages,
  };
}
